import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { colorPalette } from '@repo/mui-config/color-palette';
import {
  calculateAndConvertLoanToMoney,
  convertNumberToLocaleMoneyFormat,
} from '@repo/shared/helpers';

import { useAgreementAndApprovalsContext } from '../context/use-agreement-and-approvals-context';
import type {
  AgreementAndApprovalsDetails,
  AgreementAndApprovalsFields,
} from '../agreement-and-approvals.schema';

interface LoanSummaryDetailsProps {
  details: AgreementAndApprovalsDetails;
}
export function LoanSummaryDetails({ details }: LoanSummaryDetailsProps) {
  const { t } = useTranslation();
  const { period, payoff, pricing } = details;
  const { watch } = useFormContext<AgreementAndApprovalsFields>();
  const editedAmount = watch('editedAmount');
  const originalAmount = watch('originalAmount');
  const { setIsAdvancePaymentCalculating, isAdvancePaymentCalculating, isEditMode } =
    useAgreementAndApprovalsContext();
  const { amountFull, costIncluded } = calculateAndConvertLoanToMoney(
    Number(isEditMode ? editedAmount : originalAmount),
    pricing,
  );
  const [loanDetailsData, setLoanDetailsData] = useState({
    amountFull,
    costIncluded,
  });
  useEffect(() => {
    if (!isAdvancePaymentCalculating) {
      setLoanDetailsData({
        amountFull,
        costIncluded,
      });
    }
  }, [
    amountFull,
    costIncluded,
    editedAmount,
    isAdvancePaymentCalculating,
    originalAmount,
    setIsAdvancePaymentCalculating,
  ]);

  const loanDetails = [
    {
      title: t('loanProcess.loanSteps.agreementAndApprovals.summary.details.repaymentPeriod'),
      value: t('loanProcess.loanSteps.agreementAndApprovals.summary.details.months', {
        number: period,
      }),
    },
    {
      title: t('loanProcess.loanSteps.agreementAndApprovals.summary.details.interestRates'),
      value: `${pricing} %`,
    },
    {
      title: t('loanProcess.loanSteps.agreementAndApprovals.summary.details.payoff'),
      value: `${payoff} %`,
    },
    {
      title: t('loanProcess.loanSteps.agreementAndApprovals.summary.details.loanCost'),
      value: `${convertNumberToLocaleMoneyFormat(loanDetailsData.costIncluded, {
        decimalsDisplay: 'FRACTION_FIXED',
      })} ${details.currency}`,
    },
    {
      title: t('loanProcess.loanSteps.agreementAndApprovals.summary.details.totalRepaymentAmount'),
      value: `${convertNumberToLocaleMoneyFormat(loanDetailsData.amountFull, {
        decimalsDisplay: 'FRACTION_FIXED',
      })}  ${details.currency}`,
    },
  ];

  return (
    <List sx={{ marginTop: 2, p: 0 }}>
      <>
        {loanDetails.map((elem, index) => (
          <ListItem
            key={elem.title}
            sx={{
              backgroundColor: index % 2 !== 0 ? colorPalette.cloud.light : undefined,
              borderRadius: '10px',
              p: 1,
            }}
          >
            <Grid container justifyContent="space-between" sx={{ width: '100%' }}>
              <Grid>
                <Typography color={colorPalette.ink.dark}>{elem.title}</Typography>
              </Grid>

              <Grid>
                <Typography variant="body2">{elem.value}</Typography>
              </Grid>
            </Grid>
          </ListItem>
        ))}
      </>
    </List>
  );
}
